/**
 * NOTE: This module is going to be deprecated soon in favor of the new carts and checkout modules.
 * @deprecated
 */
import Analytics from '@monolith/legacy/services/analytics';
import ProductRemovedFromCartEvent from '@monolith/legacy/services/analytics/events/product-removed-from-cart-event';
import { ProductProperty } from '@monolith/legacy/services/analytics/properties/product-property';
import ProductQuantityInCartChangedEvent from '@monolith/legacy/services/analytics/events/product-quantity-changed-event';
import ProductAddedToCartEvent from '@monolith/legacy/services/analytics/events/product-added-to-cart-event';
import CartReachedMinimumEvent from '@monolith/legacy/services/analytics/events/cart-reached-minimum-event';
import { Commit, Dispatch } from 'vuex';
import { Cart, CartTracking, CartType, Checkout, NeededInformations } from '@monolith/legacy/types/cart';
import { CartProperty } from '@monolith/legacy/services/analytics/properties/cart-property';
import CartSavedForLaterEvent from '@monolith/legacy/services/analytics/events/cart-saved-for-later-event';
import CartRestoredEvent from '@monolith/legacy/services/analytics/events/cart-restored-event';
import { EoriCheckoutBypassFailure } from '@monolith/legacy/services/analytics/events/eori/eori-checkout-bypass-failure.event';
import { CartItemRemovedDueToMandatoryEori } from '@monolith/legacy/services/analytics/events/eori/cart-item-removed-due-to-mandatory-eori.event';
import { EoriCheckoutEntryError } from '@monolith/legacy/services/analytics/events/eori/eori-checkout-entry-error.event';
import { EoriCheckoutEntrySuccess } from '@monolith/legacy/services/analytics/events/eori/eori-checkout-entry-success.event';
import { TaxNumberType } from '@monolith/legacy/types/tax-number';
import { ActionName } from '@monolith/legacy/store/account/business/actions';
import { CartState } from '@monolith/legacy/store/cart/index';
import * as Mutations from './mutation-types';
import {
  addPromocode,
  applyCredit,
  BulkUpdate,
  deletePromocode,
  getCartItemsCount,
  getCheckout,
  getCurrentCheckout,
  NewBulkUpdate,
  removeRemoteBrand,
  saveCartForLater,
  sendRecargoEnabled,
  sendTaxNumber,
  updateItemsBulk,
} from '@monolith/legacy/services/api/cart';
import { FeatureFlag, isEnabled } from '@monolith/legacy/services/features';
import { debounce } from '@core/utilities/lodash-utils';
import Getters from '@monolith/legacy/store/cart/getters';
import { capturePurchasingException } from '@core/plugins/sentry/helper';
import { getPersonalDiscount } from '@monolith/legacy/services/api/brand-discount';
import { BrandDiscount, BrandDiscountContext } from '@monolith/legacy/types/api/brand-discount';
import { MOVE_CART } from './mutation-types';
import { getGlobalConfig } from '@monolith/legacy/services/global-config';
import { productsCount } from '@monolith/legacy/modules/cart-2/composables/use-counters';

const BULK_UPDATE_TIMEOUT = 2000;

const cartActions = {
  cartUpdateBulk: debounce(async ({ commit, getters, dispatch }) => {
    const updates = getters.getUpdates;

    if (updates.length === 0) {
      return;
    }

    try {
      commit(Mutations.SET_TOTALS_LOADING, true);
      await updateItemsBulk(updates);
      dispatch('getItemsCount');
    } finally {
      commit(Mutations.RESET_BULK_UPDATES);
      commit(Mutations.SET_TOTALS_LOADING, false);
      commit(Mutations.SET_CART_LOADING, false);
    }
  }, BULK_UPDATE_TIMEOUT),

  cartBulkUpdate: async (
    { dispatch, commit },
    { update, cartType = CartType.Main }: { update: BulkUpdate; cartType?: CartType }
  ) => {
    commit(Mutations.SET_CART_LOADING, true);
    commit(Mutations.UPDATE_BULK, update);
    commit(Mutations.SET_TOTALS_LOADING, true);
    dispatch('cartUpdateBulk', { cartType });
  },

  async deleteItem({ dispatch, commit, getters, rootGetters }, { brand, item, cartType }) {
    const cart = { ...getters.cartByBrandId(brand.id) };
    commit(Mutations.DELETE_ITEM, { brand, item, cartType });

    await Analytics.track(
      new ProductRemovedFromCartEvent(
        {
          id: item.product.id,
          name: item.product.name,
          wholesalePrice: item.wholesale_price,
          position: item.position,
          uuid: item.product.uuid,
          option: {
            id: item.option.id,
            name: item.option.name,
          },
          variant: {
            id: item.variant?.id,
            name: item.variant?.name,
          },
        } as ProductProperty,
        brand,
        {
          id: cart.id,
          totalAmountWithVat: cart.total_amount_with_vat,
        }
      )
    );

    dispatch('trackMinimumReached', cart);

    if (isEnabled(FeatureFlag.MandatoryEori) && cart.shipping_is_restricted_by_eori) {
      await Analytics.track(
        new CartItemRemovedDueToMandatoryEori({
          productId: item.product.id,
          optionId: item.option.id,
          variantId: item.variant?.id,
          checkoutId: getters.checkout.id,
          retailerId: rootGetters.retailer.id,
        })
      );
      dispatch('clearEoriErrors');
    }

    const update: BulkUpdate = {
      ...{ order_item_id: item.id },
      quantity: 0,
    };
    dispatch('cartBulkUpdate', { update });
  },
  async updateItemQuantity({ dispatch, commit, getters }, { brand, item, quantity, cartType = CartType.Main }) {
    try {
      commit(Mutations.CHANGE_ITEM_QUANTITY, { brand, item, quantity, cartType });
    } catch (e) {
      capturePurchasingException(e);
    }
    if (cartType === CartType.SavedForLater) {
      commit(Mutations.CALCULATE_BRANDS_TOTALS, { cartType });
    } else {
      dispatch('calculateCheckout');
    }
    dispatch('clearEoriErrors');
    const cart = getters.cartByBrandId(brand.id);
    Analytics.track(
      new ProductQuantityInCartChangedEvent(
        {
          id: item.product.id,
          name: item.product.name,
          wholesalePrice: item.wholesale_price,
          position: item.position,
          uuid: item.product.uuid,
          option: {
            id: item.option.id,
            name: item.option.name,
          },
          variant: {
            id: item.variant?.id,
            name: item.variant?.name,
          },
        } as ProductProperty,
        quantity * item.option.unit_multiplier,
        brand,
        {
          id: cart.id,
          totalAmountWithVat: cart.total_amount_with_vat,
        }
      )
    );
    dispatch('trackMinimumReached', cart);

    let update: BulkUpdate | NewBulkUpdate;

    // NOTE: By using Quick add item could be not yet in the cart, but the retail customer can increase the quantity
    if (item.id !== null) {
      update = {
        ...{ order_item_id: item.id },
        quantity,
      };
    } else {
      update = {
        option_id: item.option.id,
        product_variant_id: item.variant?.id,
        quantity,
      };
    }

    dispatch('cartBulkUpdate', { update, cartType });
  },
  async addToCart({ dispatch, getters, commit, rootGetters }, { brand, option, variant, quantity, noTrack }) {
    commit(Mutations.SET_CART_LOADING, true);

    const product = rootGetters.productByOptionId(option.id);
    if (product) {
      commit(Mutations.ADD_ITEM, {
        brand,
        option,
        variant,
        product,
        quantity,
      });

      dispatch('calculateCheckout');
      const cart = getters.cartByBrandId(brand.id);
      const sectionId = rootGetters['tracking/sectionId'];
      const discountIntent = rootGetters['tracking/discountIntent'](product.uuid);
      if (!noTrack) {
        await Analytics.track(
          new ProductAddedToCartEvent(
            {
              id: product.id,
              name: product.name,
              wholesalePrice: product.wholesale_price,
              option,
              variant,
              uuid: product.uuid,
            } as ProductProperty,
            quantity * product.unit_multiplier,
            brand,
            {
              id: cart.id,
              totalAmountWithVat: cart.total_amount_with_vat,
            },
            sectionId,
            discountIntent
          )
        );
        dispatch('tracking/resetSectionId', null, { root: true });
        // Only clear the stamp when THIS add-to-cart is the one that consumed it - otherwise an
        // unrelated add (e.g. quick-adding a different product B that was never clicked) would
        // wipe an earlier, still-unconsumed stamp for product A before A itself is added.
        if (discountIntent) {
          dispatch('tracking/resetDiscountIntent', null, { root: true });
        }
      }
      dispatch('trackMinimumReached', cart);
      commit(Mutations.SET_CART_LOADING, false);
    }

    const update: BulkUpdate = {
      option_id: option.id as number,
      product_variant_id: variant?.id,
      quantity,
    };
    dispatch('cartBulkUpdate', { update });
  },
  async trackMinimumReached({ commit, getters }, cart) {
    if (!cart.minimum?.above && getters.hasEventMinimumReachedSent(cart.id)) {
      return commit(Mutations.CART_MINIMUM_HAS_GONE, cart.id);
    }

    if (cart.minimum.above && !getters.hasEventMinimumReachedSent(cart.id)) {
      await Analytics.track(
        new CartReachedMinimumEvent(
          {
            id: cart.id,
            totalAmountWithVat: cart.total_amount_with_vat,
          },
          cart.brand
        )
      );
      commit(Mutations.CART_MINIMUM_REACHED, cart.id);
    }
  },
  async deletePromocode({ dispatch }, { checkoutId, promocodeId }) {
    await deletePromocode(checkoutId, promocodeId);
    dispatch('getCheckout', { checkoutId: checkoutId });
  },
  async addPromocode({ dispatch, commit }, { code, checkoutId }) {
    const res = await addPromocode(checkoutId, code);
    if (res?.response?.data?.errorMessage) {
      commit(Mutations.SET_LAST_PROMOCODE_CALL_RESULT, {
        code,
        errorMessage: res.response.data.errorMessage,
      });
      return;
    }

    commit(Mutations.SET_LAST_PROMOCODE_CALL_RESULT, {
      code,
      errorMessage: '',
    });

    dispatch('getCheckout', { checkoutId: checkoutId });
  },
  async applyCredit({ dispatch }, { amount, checkoutId }) {
    await applyCredit(amount);
    dispatch('getCheckout', { checkoutId: checkoutId });
  },
  refreshCheckout({ state, commit }, checkout) {
    // when the answer comes from the api, if another call to the api has been made we want to invalidate
    if (state.apiCallCount > 0) {
      return;
    }
    commit(Mutations.REFRESH_CHECKOUT, checkout);
    commit(Mutations.SET_TOTALS_LOADING, false);
    commit(Mutations.SET_CART_LOADING, false);
  },
  async getCheckout({ dispatch }, { checkoutId }): Promise<Checkout> {
    const checkout = await getCheckout(checkoutId);
    await dispatch('refreshCheckout', checkout);
    return checkout;
  },

  async getCurrentCheckoutWithTotals({ dispatch, commit, getters }, withLoader = true): Promise<Checkout | undefined> {
    if (withLoader) {
      commit(Mutations.SET_TOTALS_LOADING, true);
    }

    try {
      const currentCheckout = await getCurrentCheckout();

      if (!currentCheckout) {
        return undefined;
      }

      const tracking: CartTracking = {
        minimumReachedEventsSent: [...getters.aboveCarts.map((cart) => cart.id)],
      };

      commit(Mutations.SET_TRACKING, tracking);
      await dispatch('refreshCheckout', currentCheckout);
      await dispatch('initBillingWrapperState');

      return currentCheckout;
    } finally {
      if (withLoader) {
        commit(Mutations.SET_TOTALS_LOADING, false);
      }
    }
  },
  async getCurrentCheckout({ state, commit }): Promise<void> {
    // when the answer comes from the api, if another call to the api has been made we want to invalidate
    if (state.apiCallCount > 0) {
      return;
    }

    commit(Mutations.SET_CART_LOADING, true);

    try {
      //replace with cart api call
      // const checkout = await getFloatingCart();
      // if (checkout) {
      //   await dispatch(REFRESH_CHECKOUT, checkout);
      // }
    } finally {
      commit(Mutations.SET_CART_LOADING, false);
      commit(Mutations.SET_TOTALS_LOADING, false);
    }
  },
  toggleSaveCartForLater({ commit, dispatch }, cart) {
    dispatch('clearEoriErrors');
    return commit(Mutations.TOGGLE_CART_SAVE_FOR_LATER, cart);
  },

  // NEW ACTION FOR SAVE FOR LATER / MAIN CART
  async persistSaveCartForLater(_, { cart, isGotoSavedForLater = true }: { cart: Cart; isGotoSavedForLater: boolean }) {
    try {
      await saveCartForLater(cart.brand.id, isGotoSavedForLater);
    } catch (e) {
      capturePurchasingException(e);
      return Promise.reject(e);
    }
    return Promise.resolve();
  },

  /**
   * Persists a cart to be saved for later / back in the main cart
   *
   * @param {ActionContext} [vuexContext] - The Vuex action context
   * @param {Object} payload - The action payload
   * @param {Cart} payload.cart - The cart to be saved for later
   * @param {number} payload.checkoutId - The checkout id
   * @param {boolean} [payload.isGotoSavedForLater] - Whether to go to the saved for later or main cart. Optional
   * @param {boolean} [payload.track] - Whether to track the event. Optional
   * @returns resoled Promise
   *
   */
  async saveForLater(
    { dispatch, commit, state }: { dispatch: Dispatch; commit: Commit; state: CartState },
    {
      cart,
      isGotoSavedForLater = null,
      track = true,
    }: {
      cart: Cart;
      isGotoSavedForLater: boolean | null;
      track: boolean;
    }
  ) {
    //Todo: check if this is needed
    await dispatch('showProgressBar', undefined, { root: true });

    if (cart.minimum.above) {
      commit(Mutations.SET_TOTALS_LOADING, true);
    }

    // When not specified, toggle the cart to the other cart tab.
    isGotoSavedForLater = isGotoSavedForLater ?? !state.isSavedForLaterDisplayed;

    const cartProperty: CartProperty = {
      id: cart.id,
      totalAmountWithVat: cart.total_amount_with_vat,
    };

    if (track) {
      Analytics.track(
        isGotoSavedForLater
          ? new CartSavedForLaterEvent(cartProperty, cart.brand)
          : new CartRestoredEvent(cartProperty, cart.brand)
      );
    }

    try {
      await dispatch('persistSaveCartForLater', { cart, isGotoSavedForLater });
    } catch (_e) {
      // toDo. Add error handling
    }

    dispatch('getItemsCount');

    if (!cart.minimum.above) {
      return commit(MOVE_CART, { cart, isGotoSavedForLater });
    }
    await dispatch('getCurrentCheckoutWithTotals', false);
    return commit(Mutations.SET_TOTALS_LOADING, false);
  },
  removeLocalCart({ commit }, cartId: number) {
    return commit(Mutations.REMOVE_LOCAL_CART, cartId);
  },
  async removeRemoteBrand({ dispatch }: { dispatch: Dispatch }, brandId: number) {
    await dispatch('showProgressBar', undefined, { root: true });

    try {
      await removeRemoteBrand(brandId);
    } catch {
      return dispatch('scrollToError');
    }
  },
  scrollToError() {
    window.scrollTo(0, 0);
  },
  setTaxNumberErrors({ commit }, payload) {
    commit(Mutations.SET_TAX_NUMBER_ERRORS, payload);
  },
  setRecargoErrors({ commit }, payload) {
    commit(Mutations.SET_RECARGO_ERRORS, payload);
  },
  setEoriErrors({ commit }, payload) {
    commit(Mutations.SET_EORI_ERRORS, payload);
  },
  changeRecargoEnabled({ commit, dispatch }, payload) {
    dispatch('clearRecargoErrors');
    commit(Mutations.CHANGE_RECARGO_ENABLED, payload);
  },
  clearTaxNumberErrors({ commit }) {
    commit(Mutations.CLEAR_TAX_NUMBER_ERRORS);
  },
  setCompanyIdErrors({ commit }, payload) {
    commit(Mutations.SET_COMPANY_ID_ERRORS, payload);
  },
  clearCompanyIdErrors({ commit }) {
    commit(Mutations.CLEAR_COMPANY_ID_ERRORS);
  },
  setTaxNumber({ commit }, payload) {
    commit(Mutations.SET_TAX_NUMBER, payload);
  },
  clearRecargoErrors({ commit }) {
    commit(Mutations.CLEAR_RECARGO_ERRORS);
  },
  clearEoriErrors({ commit }) {
    commit(Mutations.CLEAR_EORI_ERRORS);
  },
  setEoriSuccess({ commit }, payload) {
    commit(Mutations.SET_EORI_SUCCESS, payload);
  },
  async sendTaxNumber({ dispatch, state }: { dispatch: Dispatch; state: CartState }) {
    const taxNumber = state.taxNumber;
    try {
      const result = await sendTaxNumber(taxNumber.type, taxNumber.value);

      if ('DE' !== state.checkout.retailer_country?.iso_code) {
        return dispatch('proceedToCheckout');
      }

      await dispatch('replaceRetailer', result, { root: true });
      await dispatch('setTaxNumberValue', {
        value: result.business.tax_number,
      });

      await dispatch('scrollToError');
      await dispatch('setCompanyIdErrors');
      return dispatch('setBusinessValidationStep', NeededInformations.CompanyId);
    } catch ({ response: { data } }) {
      await dispatch('scrollToError');
      await dispatch('setTaxNumberErrors', data.errors[taxNumber.type]);
    }
  },
  async sendSoleTraderDetails({ dispatch }: { dispatch: Dispatch }) {
    try {
      await dispatch('account/soleTrader/sendSoleTraderDetails', null, {
        root: true,
      });
      return dispatch('proceedToCheckout');
    } catch {
      return dispatch('scrollToError');
    }
  },
  proceedToCheckout({ getters }, proceedToCheckout = true) {
    if (!getters.hasErrors && proceedToCheckout) {
      window.location.href = '/checkout';
    }
  },
  setTaxNumberType({ commit }: { commit: Commit }, type: TaxNumberType | null) {
    return commit(Mutations.SET_TAX_NUMBER, {
      type,
      value: null,
      isSkipped: false,
      isValid: false,
    });
  },

  /**
   * NOT USED ????!!!!!!
   */
  async setProvidingTaxNumberChoice({ dispatch, getters }, isSkipped: boolean) {
    await dispatch('clearTaxNumberErrors');
    if (!isSkipped) {
      await dispatch('setBusinessValidationStep', NeededInformations.TaxNumber);
    } else {
      if (getters.canRetailerBeSoleTrader) {
        await dispatch('setBusinessValidationStep', NeededInformations.SoleTrader);
      } else {
        await dispatch('toggleSkipTaxNumber', isSkipped);
        return dispatch('setBusinessValidationStep', NeededInformations.None);
      }
    }
    return dispatch('toggleSkipTaxNumber', isSkipped);
  },
  toggleSkipTaxNumber({ commit }, isSkipped: boolean) {
    commit(Mutations.TOGGLE_SKIP_TAX_NUMBER, isSkipped);
  },
  setBusinessValidationStep({ commit }, step: NeededInformations) {
    commit(Mutations.SET_BUSINESS_VALIDATION_STEP, step);
  },
  initTaxNumberType({ state, dispatch }) {
    if (!getGlobalConfig().countries_config[state.checkout?.retailer_country?.iso_code]) {
      return Promise.reject('RETAILER COUNTRY NOT OPEN');
    }
    const type =
      getGlobalConfig().countries_config[state.checkout.retailer_country?.iso_code].business_identifier_type ===
      TaxNumberType.VatNumber
        ? TaxNumberType.TaxNumber
        : TaxNumberType.VatNumber;

    return dispatch('setTaxNumberType', type);
  },
  setTaxNumberValue({ commit, dispatch, state }, { forceStep, ...value }) {
    commit(Mutations.SET_TAX_NUMBER_VALUE, value);
    if (forceStep && forceStep !== state.businessValidationStep) {
      return dispatch('setBusinessValidationStep', forceStep);
    }
  },
  async calculateCheckout({ commit }) {
    commit(Mutations.CALCULATE_BRANDS_TOTALS, { cartType: CartType.Main });
    commit(Mutations.CALCULATE_CHECKOUT);
  },
  async initBillingWrapperState({
    state,
    dispatch,
    getters,
    rootGetters,
  }: {
    state: CartState;
    dispatch: Dispatch;
    getters: typeof Getters;
    rootGetters: any; // eslint-disable-line @typescript-eslint/no-explicit-any
  }) {
    await dispatch('initTaxNumberType');

    if (rootGetters.retailer.business.tax_number && state.taxNumber.type === TaxNumberType.TaxNumber) {
      await dispatch('setTaxNumberValue', {
        value: rootGetters.retailer.business.tax_number,
      });

      if (!getters.canRetailerBeSoleTrader) {
        return;
      }
    }

    if (state.taxNumber.type === TaxNumberType.VatNumber) {
      let vatNumber = rootGetters.retailer.business.vat_number;
      if (!vatNumber && 'CH' === state.checkout.retailer_country.iso_code) {
        let suffix = 'TVA';
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if ((this as any).$lang === 'de') {
          suffix = 'MWST';
        }

        vatNumber = `${rootGetters.retailer.business.tax_number} ${suffix}`;
      }

      if (vatNumber) {
        await dispatch('setTaxNumberValue', {
          value: vatNumber,
        });
      }

      if (rootGetters.retailer.business.vat_number) {
        return;
      }
    }
    const businessValidationStep =
      state.taxNumber.value === null || !getters.canRetailerBeSoleTrader
        ? NeededInformations.TaxNumber
        : NeededInformations.CompanyId;
    if (businessValidationStep === NeededInformations.CompanyId) {
      await dispatch('setCompanyIdErrors');
    }
    return dispatch('setBusinessValidationStep', businessValidationStep);
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async sendRecargoInformation({ dispatch, state, rootGetters }: { dispatch: Dispatch; state: CartState; rootGetters: any }) {
    const recargo = state.recargoEnabled ?? rootGetters['account/business/isPayingRecargo'];

    try {
      await sendRecargoEnabled(recargo);
    } catch ({ response: { data } }) {
      await dispatch('scrollToError');
      await dispatch('setRecargoErrors', data.errors.is_paying_recargo);
    }
  },
  async sendEoriInformation({
    dispatch,
    state,
    getters,
    rootGetters,
  }: {
    dispatch: Dispatch;
    state: CartState;
    getters: any; // eslint-disable-line @typescript-eslint/no-explicit-any
    rootGetters: any; // eslint-disable-line @typescript-eslint/no-explicit-any
  }) {
    await dispatch('clearEoriErrors');
    const eori = state.eori;

    try {
      await dispatch(`account/business/${ActionName.UpdateEoriNumber}`, eori, {
        root: true,
      });

      await dispatch('setEoriSuccess', true);

      Analytics.track(
        new EoriCheckoutEntrySuccess({
          checkoutId: getters.checkout.id,
          retailerId: rootGetters.retailer.id,
          value: eori,
        })
      );
    } catch ({ response: { data } }) {
      await dispatch('scrollToError');
      await dispatch('setEoriErrors', data.errors?.eori_number);

      if (eori?.length === 0) {
        // User attempted to skip page without entering number.
        Analytics.track(
          new EoriCheckoutBypassFailure({
            checkoutId: getters.checkout.id,
            retailerId: rootGetters.retailer.id,
          })
        );
      } else {
        // User entered an invalid value.
        Analytics.track(
          new EoriCheckoutEntryError({
            checkoutId: getters.checkout.id,
            message: data.errors?.eori_number?.toString(),
            retailerId: rootGetters.retailer.id,
            value: eori,
          })
        );
      }
    }
  },
  async validateCart({ state, getters, dispatch }, proceedToCheckout = true) {
    await dispatch('clearTaxNumberErrors');
    await dispatch('account/soleTrader/clearSoleTraderDetailsErrors', null, {
      root: true,
    });
    await dispatch('clearCompanyIdErrors');
    if (getters.shouldShowRecargoQuestion) {
      await dispatch('sendRecargoInformation');
    }

    if (getters.shouldShowEoriQuestion) {
      await dispatch('sendEoriInformation');
    }

    if (state.businessValidationStep === NeededInformations.None) {
      await dispatch('updateVatExemption');
      return dispatch('proceedToCheckout', proceedToCheckout);
    }
    switch (state.businessValidationStep) {
      case NeededInformations.TaxNumber:
        if (getters['shouldDisplayCheckoutSoleTraderUk'] && !state.taxNumber.value) {
          return dispatch('sendSoleTraderDetails');
        }
        if (state.taxNumber.isSkipped) {
          return dispatch('proceedToCheckout', proceedToCheckout);
        }
        if (state.taxNumber.type === TaxNumberType.VatNumber) {
          await dispatch('updateVatExemption');
        }
        return dispatch('sendTaxNumber');
      case NeededInformations.SoleTrader:
      case NeededInformations.CompanyRegistration:
        return dispatch('sendSoleTraderDetails');
    }
  },
  async getBrandsDiscounts({ commit }, brandIds: number[]) {
    let discounts: BrandDiscount[] = [];
    try {
      discounts = await getPersonalDiscount(BrandDiscountContext.cartPage, brandIds);
      commit(Mutations.SET_BRANDS_DISCOUNTS, discounts);
    } catch (e) {
      capturePurchasingException(e.message);
    }
  },
  async changeTab({ commit }, tab: CartType) {
    commit(Mutations.SET_CURRENT_TAB, tab === CartType.SavedForLater);
  },
  async getItemsCount({ rootState, commit }) {
    const itemsCount = await getCartItemsCount(rootState.user.business.retailer.uuid);
    productsCount.value = itemsCount.main;
    commit(Mutations.SET_ITEMS_COUNT, itemsCount);
    return itemsCount;
  },
  async updateVatExemption({ getters, dispatch }) {
    return await dispatch(
      `account/business/${ActionName.UpdateVatInfo}`,
      {
        aboveIntracommThreshold: false,
        vatExemption: getters['getVatExemption'],
      },
      { root: true }
    );
  },
};

export default cartActions;
export type CartActions = typeof cartActions;
